/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useRef, useState } from 'react'
import uniqueId from 'lodash/uniqueId'
import { ChartProvider, useTooltip, useZoom } from './Context/ChartContext.tsx'
import PopulateDefaults from './Defaults/Settings'

// VISUAL COMPONENT HELPERS
import NamePlate from './Header/NamePlate'
import Border from './Header/Border'
import Stripes from './Defs/Stripes'
import DoubleStripes from './Defs/DoubleStripes'
import Textures from './Defs/Textures'
import FuzzFilter from './Defs/FuzzFilter.tsx'
import SourcePlate from './Footer/SourcePlate'

import Chart from './Chart.tsx'

import './Stylesheets/styles.css'

function getTranslate(localPoint, newZoom) {
    return {
        x: -localPoint.x * (newZoom - 1),
        y: -localPoint.y * (newZoom - 1),
    }
}

const chartDefaults = {
    shape: 'snap',
    shapeRatio: 1,
    charts: [],
    namePlate: {
        show: true,
        background: 'stripes',
        backgroundColor: '#fff',
        titleFontColor: '#000',
        primaryTitleText: '',
        secondaryTitleText: '',
        secondaryFontColor: '#a9a9a9',
        tertiaryTitleText: '',
        tertiaryFontColor: '#000',
    },
    sourcePlate: {
        show: true,
        background: 'none',
        backgroundColor: '#c2c2c2',
        siteTitle: 'SAVANT',
        siteColor: '#32858d',
        sourceTitle: 'SOURCE: MLB.COM',
        sourceColor: '#000',
    },
    venueId: '7',
    mainPanelBackground: 'none',
    showToolTip: false,
    embedStyle: {},
    pitcherPerspective: false,
}

function VisualContent({ data, chart }) {
    const initialZoom = 1

    const { zoom, translate, setZoom } = useZoom()
    const zoomRef = useRef(null)
    const { tooltipRef } = useTooltip()

    function handleResetZoom(e) {
        e.stopPropagation()

        const centerish = new DOMPoint(250, 250)
        setZoom(initialZoom, getTranslate(centerish, initialZoom))
    }

    // function handleRightMouseClick(e) {
    //     // Probably bad
    //     e.preventDefault()

    //     // New API for me, thanks AI
    //     const point = new DOMPoint(e.clientX, e.clientY)

    //     const globalToLocalMatrix = zoomRef.current.getScreenCTM().inverse()

    //     const localPoint = point.matrixTransform(globalToLocalMatrix)

    //     const newZoom = Math.max(Math.min(15, zoom / 1.5), initialZoom)

    //     const newTranslate = getTranslate(localPoint, newZoom)
    //     setZoom(newZoom, newTranslate)
    // }

    // Settings JSON
    // Powers the entire visualization
    const settings = PopulateDefaults(data, chart)

    const handlePitchClick = useCallback(
        (e) => {
            // TODO: use a chart option to enable/disable zoom
            if (!settings.enableZoom) {
                return
            }

            // New API for me, thanks AI
            const point = new DOMPoint(e.clientX, e.clientY)

            const globalToLocalMatrix = zoomRef.current.getScreenCTM().inverse()

            const localPoint = point.matrixTransform(globalToLocalMatrix)
            // Make the first step relatively big, then meander in for a bit until 15x
            const newZoom = Math.max(Math.min(50, zoom * 5), initialZoom)

            const newTranslate = getTranslate(localPoint, newZoom)
            setZoom(newZoom, newTranslate)
        },
        [zoom, settings]
    )

    const [id] = useState(uniqueId('svg-'))
    return (
        <svg
            key="svg_source"
            id={id}
            width={settings.ratioWidth}
            height={settings.ratioHeight}
            viewBox={`0 0 ${settings.svgWidth} ${settings.svgHeight}`}
            // onClick={handlePitchClick}
            // onContextMenu={handleRightMouseClick}
        >
            <defs key="defs">
                <Stripes />
                <DoubleStripes />
                <Textures />
                <FuzzFilter />

                {chart.embedStyle ? (
                    <style
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: chart.embedStyle }}
                    />
                ) : null}
            </defs>
            <Border key="border" settings={settings} />
            <g
                className="evp-zoom-wrapper"
                ref={zoomRef}
                transform={`translate(${translate.x}, ${translate.y}) scale(${zoom})`}
            >
                <g
                    key="gHolder"
                    // transform={`scale(${1}) translate(0, 0)`}
                    transform={`scale(${
                        settings.vizScaleOverride || settings.vizScale
                    }) translate(${settings.vizPadding.left},${
                        settings.vizPadding.top
                    })`}
                >
                    <rect
                        key="rect-background"
                        width={settings.vizWidth}
                        height={settings.vizHeight}
                        fill="none"
                        stroke="none"
                    />
                    <g
                        key="rect-transform"
                        transform={settings.svgZoom || null}
                    >
                        {settings.charts.map((c, i) => (
                            <Chart
                                key={`chart-${i}`}
                                chartIndex={i}
                                settings={settings}
                                onPitchClick={handlePitchClick}
                            />
                        ))}
                    </g>
                    <g
                        key="tooltip"
                        ref={tooltipRef}
                        style={{
                            display: chart.showToolTip ? 'block' : 'none',
                        }}
                    />
                </g>
            </g>
            {zoom !== initialZoom && (
                <g transform="translate(10, 120)">
                    <text fontSize="20" onClick={handleResetZoom}>
                        Reset Zoom
                    </text>
                </g>
            )}
            {chart.namePlate.show ? <NamePlate settings={settings} /> : null}
            {chart.sourcePlate.show ? (
                <SourcePlate settings={settings} />
            ) : null}
        </svg>
    )
}

export default function Visual({ data = [], chart = chartDefaults }) {
    const settings = useMemo(() => PopulateDefaults(data, chart), [data, chart])

    return (
        <ChartProvider initialSettings={settings}>
            <VisualContent data={data} chart={chart} settings={settings} />
        </ChartProvider>
    )
}
